/**
 * PlatformSelection.tsx
 * Manages the selection of distribution platforms for the release.
 * Includes a select all option and individual platform toggles.
 * Filters DSPs based on user's company's available DSPs.
 */

import React from 'react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { PlatformSelectionProps } from '../../types/components';
import { SelectedDSPs } from '../../types/form';
import { Skeleton } from '../../../components/ui/skeleton';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { validateSelectedDSPs } from '../../utils/dspValidation';
import { useAppSelector } from '../../../hooks/redux';
import Button from '../../../components/ui/Button';


interface Platform {
  id: keyof SelectedDSPs;
  label: string;
  apiName: string; // The name as stored in the database
}

const PlatformSelection: React.FC<PlatformSelectionProps> = ({
  selectedDSPs,
  setSelectedDSPs,
  onDspDeselect,
  onEmailSelect,
  dspList,
}) => {
  const navigate = useNavigate();
  const { email: emailPermission } = useUserPermissions();
  const hasEmailAccess = !!emailPermission && emailPermission.limit !== 0;
  /*
   * The company's DSP access comes from the profile that AuthenticatedLayout
   * already fetches into the store on load, so this reads the store rather
   * than calling /users/me a second time for the same payload. `userLoaded`
   * separates "profile still in flight" from "profile arrived with no DSPs",
   * which an empty list alone cannot express.
   */
  const availableDSPs = useAppSelector((state) => state.user.Label?.availableDSPs) ?? [];
  const userLoaded = useAppSelector((state) => state.user.id !== null);
  const userError = useAppSelector((state) => state.user.error);

  // Still waiting only while the profile has neither arrived nor failed —
  // otherwise a failed fetch would leave the section skeletal forever.
  const loading = !userLoaded && !userError;
  const error = userError
    ? 'Failed to load DSP access. Please try again.'
    : userLoaded && availableDSPs.length === 0
      ? 'No DSPs are available for your company. Please contact your administrator.'
      : null;

  // All possible platforms with their API names (alphabetically ordered)
  const allPlatforms: Platform[] = [
    { id: 'amazon', label: 'Amazon', apiName: 'Amazon Music' },
    { id: 'apple', label: 'Apple', apiName: 'Apple Music' },
    { id: 'audiomack', label: 'Audiomack', apiName: 'Audiomack' },
    { id: 'deezer', label: 'Deezer', apiName: 'Deezer for Creators' },
    ...(hasEmailAccess ? [{ id: 'email' as keyof SelectedDSPs, label: 'Email', apiName: 'Email' }] : []),
    { id: 'siriusxm', label: 'SiriusXM', apiName: 'SiriusXM' },
    { id: 'soundcloud', label: 'SoundCloud', apiName: 'SoundCloud' },
    { id: 'spotify', label: 'Spotify', apiName: 'Spotify' },
    { id: 'TIDAL', label: 'TIDAL', apiName: 'TIDAL' },
    { id: 'vevo', label: 'Vevo', apiName: 'Vevo' },
    { id: 'youtube', label: 'YouTube', apiName: 'YouTube' },
  ];


  // Filter platforms based on available DSPs
  const availablePlatforms = allPlatforms.filter(platform => {
    // Email should always be available
    if (platform.id === 'email') return true;

    // Check if the platform is in the user's company's available DSPs
    return availableDSPs.includes(platform.apiName);
  });

  
  const handleCheckboxChange = async (dspId: keyof SelectedDSPs) => {
    const isCurrentlySelected = selectedDSPs[dspId];

    // If deselecting and callback provided, call it to check for notes
    if (isCurrentlySelected && onDspDeselect) {
      const shouldProceed = await onDspDeselect(dspId);

      // If callback returns false, don't deselect
      if (shouldProceed === false) {
        return;
      }
    }

    // If selecting Email, call onEmailSelect callback to open the modal
    if (dspId === 'email' && !isCurrentlySelected && onEmailSelect) {
      onEmailSelect();
    }

    // If selecting a platform (other than email), validate that specific platform has a connected DSP account first
    if (!isCurrentlySelected && dspId !== 'email') {
      const { isValid } = validateSelectedDSPs({ [dspId]: true }, dspList);
      if (!isValid) {
        Swal.fire({
          title: "No connected account",
          text: `You don't have a connected account for ${dspId.toUpperCase()}. Please connect it before selecting this platform.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Connect DSPs",
          cancelButtonText: "Cancel",
        }).then(result => {
          if (result.isConfirmed) {
            navigate("/settings?tab=dsps");
          }
        });
        return;
      }
    }

    // Proceed with selection/deselection
    setSelectedDSPs(prev => ({
      ...prev,
      [dspId]: !prev[dspId]
    }));
  };
  const handleSelectAll = () => {
    const areAllSelected = availablePlatforms.every(platform => selectedDSPs[platform.id]);
    const newValue = !areAllSelected;

    const updatedSelections = { ...selectedDSPs };
    const skippedPlatforms: string[] = [];

    availablePlatforms.forEach(platform => {
      if (newValue && platform.id !== 'email') {
        const { isValid } = validateSelectedDSPs({ [platform.id]: true }, dspList);
        if (!isValid) {
          skippedPlatforms.push(platform.label);
          return;
        }
      }
      updatedSelections[platform.id] = newValue;
    });

    setSelectedDSPs(updatedSelections);
    if (hasEmailAccess && !availablePlatforms.every(platform => selectedDSPs[platform.id]) && onEmailSelect) {
      onEmailSelect();
    }

    if (skippedPlatforms.length > 0) {
      Swal.fire({
        title: "Some platforms skipped",
        text: `You don't have connected accounts for: ${skippedPlatforms.join(', ')}. Please connect these DSPs to select them.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Connect DSPs",
        cancelButtonText: "OK",
      }).then(result => {
        if (result.isConfirmed) {
          navigate("/settings?tab=dsps");
        }
      });
    }
  };

  if (loading) {
    return (
      <section
        className="p-4 bg-white border rounded-lg border-neutral-200 sm:p-6"
        aria-busy="true"
      >
        <h4 className="mb-6 text-xl font-semibold text-black">Where to pitch?</h4>
        {/*
          Placeholders shaped like the platform tiles below — same grid, same
          card height — so the section keeps its size and the tiles fill in
          rather than the form jumping when the list arrives. Six is the typical
          count; the exact number isn't known until the request resolves.
        */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-4 border rounded-lg border-neutral-200"
            >
              <Skeleton className="w-5 h-5 rounded shrink-0" />
              <Skeleton className="w-28 h-4" />
            </div>
          ))}
        </div>
        <Skeleton className="w-full h-10 rounded-md sm:w-28" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="p-4 bg-white border rounded-lg border-neutral-200 sm:p-6">
        <h4 className="mb-6 text-xl font-semibold text-black">Where to pitch?</h4>
        <div className="p-4 text-center border border-danger-200 rounded-lg bg-danger-50">
          <p className="text-danger-600">{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="p-4 bg-white border rounded-lg border-neutral-200 sm:p-6" data-tour="where-to-pitch-section">
      {/* Section Title */}
      <h4 className="mb-6 text-xl font-semibold text-black">Where to pitch?</h4>

  

      {/* Platform Checkboxes */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 md:grid-cols-3">
        {availablePlatforms.map((platform) => (
          <label
            key={platform.id}
            className="flex items-center gap-3 p-4 transition-colors border rounded-lg cursor-pointer border-neutral-200 hover:border-primary-300 hover:bg-primary-50"
          >
            <input
              type="checkbox"
              checked={selectedDSPs[platform.id]}
              onChange={() => handleCheckboxChange(platform.id)}
              className="w-5 h-5 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
            />
            <span className="text-base font-medium text-black">{platform.label}</span>
          </label>
        ))}
      </div>

      {/* Select All Button */}
      {availablePlatforms.length > 0 && (
        <Button type="button" onClick={handleSelectAll} className="w-full sm:w-auto">
          {availablePlatforms.every(platform => selectedDSPs[platform.id]) ? "Unselect All" : "Select All"}
        </Button>
      )}
    </section>
  );
  
};

export default PlatformSelection;